import { GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useShoppingCartColumns from 'src/assets/static/shopping_cart/useShoppingCartColumns';
import GridWrapper from 'src/components/GridWrapper';
import ViewRecommendedItems from 'src/pages/ViewRecommendedItems';
import { ShoppingCartProduct } from 'src/types/shopping_cart/ShoppingCart';
import { useContextSelector } from 'use-context-selector';
import { ShoppingCartContext } from './ShoppingCartContext';
import ShoppingCartProductInformationPanel from './ShoppingCartProductInformationPanel';
import { getShoppingCartProductSelection } from './shoppingCartProductMapper';
import ShoppingCartToolbarActions from './ShoppingCartToolbarActions';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100, 500],
};

const defaultProps = {
  tableTextId: 'Items',
  DataItemKey: 'productKey',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable,
  pageable: gridPagerSettings,
} as const;

const ShoppingCartListView: FC = () => {
  const { t } = useTranslation();

  const dataState = useContextSelector(ShoppingCartContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    ShoppingCartContext,
    (state) => state.onDataStateChange
  );
  const onSelectionChange = useContextSelector(
    ShoppingCartContext,
    (state) => state.onSelectionChange
  );
  const onHeaderSelectionChange = useContextSelector(
    ShoppingCartContext,
    (state) => state.onHeaderSelectionChange
  );
  const onItemChange = useContextSelector(ShoppingCartContext, (state) => state.onItemChange);
  const productSelections = useContextSelector(
    ShoppingCartContext,
    (state) => state.productSelections
  );
  const allProducts = useContextSelector(ShoppingCartContext, (state) => state.allProducts);
  const productsData = useContextSelector(ShoppingCartContext, (state) => state.products);
  const productsTotal = useContextSelector(ShoppingCartContext, (state) => state.productsTotal);
  const loadingProducts = useContextSelector(
    ShoppingCartContext,
    (state) => state.loadingProducts
  );
  const filterSearch = useContextSelector(ShoppingCartContext, (state) => state.filterSearch);
  const isSwapItemModalOpen = useContextSelector(
    ShoppingCartContext,
    (state) => state.isSwapItemModalOpen
  );
  const handleCloseSwapItemPanel = useContextSelector(
    ShoppingCartContext,
    (state) => state.handleCloseSwapItemPanel
  );
  const swapItemData = useContextSelector(ShoppingCartContext, (state) => state.swapItemData);
  const isProductPanelOpen = useContextSelector(
    ShoppingCartContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    ShoppingCartContext,
    (state) => state.handleCloseProductPanel
  );

  const catalogData = useMemo(() => {
    if (!productsData.length) {
      return [];
    }

    const cartQuantityByKey = new Map(
      allProducts.map((product) => [product.productKey, product.quantity])
    );

    return productsData.map((item: ShoppingCartProduct) => {
      const selection = getShoppingCartProductSelection(
        productSelections,
        item.productKey,
        cartQuantityByKey.get(item.productKey) ?? item.quantity ?? 1
      );

      return {
        ...item,
        selected: selection.selected,
        qty: selection.qty,
      };
    });
  }, [allProducts, productsData, productSelections]);

  const columns = useShoppingCartColumns();

  const hasFilterSearchValue = Boolean(filterSearch.trim());

  return (
    <main className="flex h-full min-h-0 min-w-0 flex-col px-4 pb-4">
      {hasFilterSearchValue && (
        <div className="mb-2 shrink-0 truncate text-sm font-[600]">
          {t('Showing results for')} &quot;{filterSearch.trim()}&quot;
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <GridWrapper
          id="shoppingCartListTable"
          className="product-list-view h-full min-h-0"
          columns={columns}
          dataSource={catalogData}
          total={productsTotal}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          height="100%"
          gridToolbarChildren={<ShoppingCartToolbarActions />}
          {...defaultProps}
          loading={loadingProducts}
          noRecordText={t('No items to display')}
          onHeaderSelectionChange={onHeaderSelectionChange}
          onSelectionChange={onSelectionChange}
          onItemChange={onItemChange}
          scrollable="scrollable"
        />
      </div>
      <ShoppingCartProductInformationPanel
        isOpen={isProductPanelOpen}
        onClose={handleCloseProductPanel}
      />
      <ViewRecommendedItems
        isOpenSidePanel={isSwapItemModalOpen}
        onCloseSidePanel={() => handleCloseSwapItemPanel()}
        isInternal={true}
        dataParams={swapItemData}
      />
    </main>
  );
};

export default ShoppingCartListView;
